import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogActions, MatDialogTitle, MatDialogContent, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { Store } from '@ngxs/store';
import { Kind } from '../../core/interfaces/kind.interface';
import { FunctionalAudio, FunctionalHelpJsonModel, FunctionalImage } from '../functionalHelp.json.model';
import { AppSelectors } from '../../app.state';
import { ConsumerJsonModel } from '../../consumer/consumer.json.model';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { ExerciseKind, getExerciseKinds, MediaType } from '../../core/types';
import { TranslationService } from '../../core/services/translationService';
import { Md5 } from 'ts-md5';
import { environment } from '../../../environments/environment';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { AssetJsonModel } from '../../asset/assets.json.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { checkForNameErrors, doesConsumerExist, doesSubjectExist, getConsumerErrorText, MIN_NAME_LENGTH } from '../../core/util';
import { AuthState } from '../../core/authentication/auth.state';
import { PixlEditorDialogComponent, PixlEditorDialogModel } from '../../pixl-editor/pixl-editor-dialog/pixl-editor-dialog.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import TurndownService from 'turndown';

@Component({
  selector: 'pixl-edit-funtional-help',
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    CommonModule,
  ],
  templateUrl: './edit-funtional-help.component.html',
  styleUrl: './edit-funtional-help.component.scss'
})
export class EditFuntionalHelpComponent implements OnInit, AfterViewInit {
  @ViewChild('desktopAudioCtrl') desktopAudioCtrl?: ElementRef;
  @ViewChild('mobileAudioCtrl') mobileAudioCtrl?: ElementRef;

  private readonly dialogRef = inject(MatDialogRef<EditFuntionalHelpComponent>);
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private appState$ = this.store.select(AppSelectors.appState);
  private translationService = inject(TranslationService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private auth$ = this.store.select(AuthState.getToken);
  // Injiziere den DomSanitizer
  private sanitizer = inject(DomSanitizer);
  private explanationContentHtml = '';
  private turndownService = new TurndownService();

  public approval = signal(false);
  public formGroup: FormGroup;
  public data = inject<FunctionalHelpJsonModel>(MAT_DIALOG_DATA);
  public consumers: ConsumerJsonModel[] = [];
  public competencies: SubjectJsonModel[] = [];
  public kinds: Kind[] = [];
  public desktopAudioUrl = signal('');
  public mobileAudioUrl = signal('');
  public imageUrl = signal('');
  public nameError = signal(false);
  public nameErrorText = signal('');
  public consumerError = signal(false);
  public subjectError = signal(false);
  public consumerIsDeleted = signal(false);
  public competencyIsDeleted = signal(false);
  public isNewRecord = signal(true);
  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';
  public viewContentHtml = signal<SafeHtml>('');

  constructor() {
    this.approval.set(this.data.approval === 'Dormant');

    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.competencies = subjects;

        // Check if consumer still exists
        if (this.data.consumerId) {
          if (!doesConsumerExist(consumers, this.data?.getConsumer())) {
            this.consumerError.set(true);
            setTimeout(() => {
              this.formGroup.patchValue({ consumerId: undefined });
            }, 0);
          }
        }

        // Check if subject/competency still exists
        if (this.data.subjectId) {
          if (!doesSubjectExist(subjects, this.data.subjectId)) {
            this.subjectError.set(true);
            setTimeout(() => {
              this.formGroup.patchValue({ subjectId: undefined });
            }, 0);
          }
        }

        getExerciseKinds().forEach((entry: ExerciseKind) => {
          let found = false;

          for (const kind of this.kinds) {
            if (kind.value.toLowerCase() === entry.toLowerCase()) {
              found = true;
              break;
            }
          }

          if (!found && entry !== 'auswahl') {
            this.kinds.push({
              value: entry,
              option: this.translationService.translate(entry)
            });
          }
        });

        this.kinds = [...this.kinds.sort((a, b) => {
          if (a.value.toLowerCase() === 'none') {
            return -1;
          }

          if (b.value.toLowerCase() === 'none') {
            return 1;
          }

          return a.option.localeCompare(b.option);
        })];
      }
    });

    let kind = undefined;
    if (this.data.exerciseKind) {
      kind = this.data.exerciseKind;
      if (this.data.exerciseSubKind) {
        kind += '.' + this.data.exerciseSubKind;
      }
    }

    this.formGroup = this.fb.group({
      name: new FormControl({ value: this.data.name || '', disabled: this.approval() }, [Validators.required, Validators.minLength(MIN_NAME_LENGTH)]),
      consumerId: new FormControl({ value: this.data.consumerId || undefined, disabled: ((this.approval() || this.data.consumerId !== undefined)) && !this.consumerError() }, Validators.required),
      subjectId: new FormControl({ value: this.data.subjectId || undefined, disabled: this.approval() }, Validators.required),
      exerciseKind: new FormControl({ value: kind || '', disabled: this.approval() }, Validators.required),
    });

    let existRecord = false;
    if (this.data.consumerId && this.data.subjectId) {
      existRecord = this.data.consumerId.length > 0 && this.data.subjectId.length > 0;
    }

    this.isNewRecord.set(!existRecord);

    // Error handling if consumer or competency was deleted
    if (this.data.consumerId && typeof (this.data?.getConsumer()?.name) === 'undefined') {
      this.formGroup.patchValue({ consumerId: undefined });
      this.consumerIsDeleted.set(true);
    }

    if (this.data.subjectId && typeof (this.data?.getCompetency()?.name) === 'undefined') {
      this.formGroup.patchValue({ subjectId: undefined });
      this.competencyIsDeleted.set(true);
    }

    this.buildURLs();
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ? '/' : 'https://zav-stage.westermann.de/';

    if (this.data.desktopAudio && this.data.desktopAudio.name) {
      let desktopAudioUrl = baseUrl + 'media/audio/';
      Md5
        .hashStr(this.data.desktopAudio.name)
        .split(/(.{8})/)
        .filter(O => O)
        .forEach((entry, idx) => {
          desktopAudioUrl += ((idx > 0) ? '/' : '') + entry.toUpperCase();
        });
      desktopAudioUrl += '.mp3';

      this.desktopAudioUrl.set(desktopAudioUrl);
    }

    if (this.data.mobileAudio && this.data.mobileAudio.name) {
      let mobileAudioUrl = baseUrl + 'media/audio/';
      Md5
        .hashStr(this.data.mobileAudio.name)
        .split(/(.{8})/)
        .filter(O => O)
        .forEach((entry, idx) => {
          mobileAudioUrl += ((idx > 0) ? '/' : '') + entry.toUpperCase();
        });
      mobileAudioUrl += '.mp3';

      this.mobileAudioUrl.set(mobileAudioUrl);
    }

    if (this.data.image && this.data.image.name) {
      let imageUrl = baseUrl;
      if (this.data.image.mediaType === 'Image') {
        imageUrl += 'media/image/';
      }

      if (this.data.image.mediaType === 'Animation') {
        imageUrl += 'media/animation/';
      }

      Md5
        .hashStr(this.data.image.name)
        .split(/(.{8})/)
        .filter(O => O)
        .forEach((entry, idx) => {
          imageUrl += ((idx > 0) ? '/' : '') + entry.toUpperCase();
        });

      if (this.data.image.mediaType === 'Image') {
        imageUrl += '.png';
      }

      if (this.data.image.mediaType === 'Animation') {
        imageUrl += '.gif';
      }

      this.imageUrl.set(imageUrl);
    }

    // Necessary, because the audio tag does not reload a new audio url by itself
    this.desktopAudioCtrl?.nativeElement?.load();
    this.mobileAudioCtrl?.nativeElement?.load();
  }

  public ngOnInit(): void {
    this.auth$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        this.authToken = token;
      });
    if (this.data.markupText) {
      this.explanationContentHtml = this.data.markupText;
      this.viewContentHtml.set(this.sanitizer.bypassSecurityTrustHtml(this.data.markupText));
    }
  }

  public ngAfterViewInit(): void {
    this.formGroup.markAllAsTouched();
  }

  public getConsumerError(): string {
    return getConsumerErrorText();
  }

  public checkName(formControl: AbstractControl): string | null {
    return checkForNameErrors(formControl);
  }

  public getDesktopAudioName(): string {
    if (this.data.desktopAudio && this.data.desktopAudio.name) {
      return this.data.desktopAudio.name;
    }
    return '';
  }

  public getMobileAudioName(): string {
    if (this.data.mobileAudio && this.data.mobileAudio.name) {
      return this.data.mobileAudio.name;
    }
    return '';
  }

  public getImageName(): string {
    if (this.data.image && this.data.image.name) {
      return this.data.image.name;
    }
    return '';
  }

  public openEditor(): void {
    const dialogRef = this.dialog.open(PixlEditorDialogComponent, {
      minWidth: '60vw',
      // width: '80vw',
      height: '70vh',
      data: {
        explanationContentHtml: this.explanationContentHtml,
        authToken: this.authToken,
        assetUrl: this.assetsUrl,
        mediaUrl: this.mediaUrl,
      }
    });
    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pixlEditorData: PixlEditorDialogModel) => {
        console.log(pixlEditorData.explanationContentHtml);
        this.explanationContentHtml = pixlEditorData.explanationContentHtml;
        this.viewContentHtml.set(this.sanitizer.bypassSecurityTrustHtml(pixlEditorData.explanationContentHtml));
        this.data.markupText = pixlEditorData.explanationContentHtml;
        this.data.markdownText = this.turndownService.turndown(pixlEditorData.explanationContentHtml);
      });
  }

  public openAudioChoice(desktop: boolean): void {
    if (desktop) {
      this.showMediumDialog('Audio', true);
    }
    else {
      this.showMediumDialog('Audio', false);
    }
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType, desktopAudio?: boolean): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: desktopAudio,
      subjectId: this.data.subjectId
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetJsonModel) => {
        if (answer) {
          switch (mediaType) {
            case 'Image': {
              let imageModel: FunctionalImage;

              if (!this.data.image) {
                imageModel = new FunctionalImage();
              }
              else {
                imageModel = this.data.image;
              }
              imageModel.id = answer.id;
              imageModel.mediaType = mediaType;
              imageModel.name = answer.name;
              this.data.image = imageModel;
              break;
            }
            case 'Audio': {
              let audioModel: FunctionalAudio;

              if (desktopAudio) {
                if (!this.data.desktopAudio) {
                  audioModel = new FunctionalAudio();
                }
                else {
                  audioModel = this.data.desktopAudio;
                }

                audioModel.id = answer.id;
                audioModel.mediaType = mediaType;
                audioModel.name = answer.name;
                this.data.desktopAudio = audioModel;
              }
              else {
                if (!this.data.mobileAudio) {
                  audioModel = new FunctionalAudio();
                }
                else {
                  audioModel = this.data.mobileAudio;
                }
                audioModel.id = answer.id;
                audioModel.mediaType = mediaType;
                audioModel.name = answer.name;
                this.data.mobileAudio = audioModel;
              }
            }
              break;
            default:
              break;
          }

          this.buildURLs();
        }
      });
  }

  public deleteMediaEntry(audio: boolean, desktopAudio: boolean): void {
    let id;
    let name;
    let headerText;

    if (audio) {
      headerText = 'Audio-Datei löschen';

      if (desktopAudio) {
        id = this.data.desktopAudio?.id;
        name = this.data.desktopAudio?.name;
      }
      else {
        id = this.data.mobileAudio?.id;
        name = this.data.mobileAudio?.name;
      }
    }

    if (!audio) {
      id = this.data.image?.id;
      name = this.data.image?.name;
      headerText = 'Bild-Datei löschen';
    }

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: id,
          name: name,
          itemType: 'Datei',
          headerText: headerText,
          deleteFn: () => {
            if (audio) {
              if (desktopAudio) {
                this.data.desktopAudio = null;
              }
              else {
                this.data.mobileAudio = null;
              }
            }

            if (!audio) {
              this.data.image = null;
            }
            dialogRef.close();
          }
        },
      });
  }

  public deleteEditorContent(): void {
    const headerText = 'Editor-Inhalt löschen';

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: '',
          name: name,
          itemType: 'Datei',
          headerText: headerText,
          deleteFn: () => {
            // Setze das Signal auf einen leeren sicheren HTML-Wert
            this.explanationContentHtml = '';
            this.viewContentHtml.set(this.sanitizer.bypassSecurityTrustHtml(''));
            dialogRef.close();
          }
        },
      });
  }

  public consumerChanged(ev: string): void {
    if (ev) {
      this.consumerIsDeleted.set(false);
    }
  }

  public competencyChanged(ev: string): void {
    if (ev) {
      this.competencyIsDeleted.set(false);
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    if (this.formGroup.valid) {
      this.data.name = this.formGroup?.get('name')?.value;
      this.data.consumerId = this.formGroup?.get('consumerId')?.value;
      this.data.subjectId = this.formGroup?.get('subjectId')?.value;
      this.data.exerciseKind = this.formGroup?.get('exerciseKind')?.value;
      this.data.markupText = this.explanationContentHtml;
      this.data.markdownText = this.turndownService.turndown(this.explanationContentHtml);
      this.dialogRef.close(this.data);
    }
  }
}
